import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IEntity } from "@odata/BindingContext";
import { IPrPayrollPeriodEntity, IPrSalaryAdvanceEntity } from "@odata/GeneratedEntityTypes";
import { handleCurrentTemporalPropChange } from "@odata/TemporalUtils";
import { TableStorageWithPayrollPeriods } from "@pages/payroll/payroll.types";
import { createDateFromPeriod, createPeriodFromDate, preselectPayrollPeriods } from "@pages/payroll/payroll.utils";
import { getSalaryAdvancePeriodFilter, MonthYearLocalPath } from "@pages/payroll/salaryAdvance/SalaryAdvance.utils";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { FormMode } from "../../../enums";
import { ModelEvent } from "../../../model/Model";
import { IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { loadAndStoreRanges, setMatchingNumberRange } from "../../numberRange/NumberRange.utils";

export interface ISalaryAdvanceFormStorageCustomData extends IFormStorageDefaultCustomData {
    existingPeriods: IPrPayrollPeriodEntity[];
}

interface IProps extends IFormViewProps<IPrSalaryAdvanceEntity, ISalaryAdvanceFormStorageCustomData> {
}

export type TSalaryAdvanceFormStorage = IProps["storage"];

class SalaryAdvanceFormView extends FormViewForExtend<IPrSalaryAdvanceEntity, IProps> {
    static contextType = AppContext;
    static defaultProps = {
        title: "SalaryAdvances:FormTitle"
    };

    componentWillUnmount() {
        super.componentWillUnmount();
        this.props.tableStorage.emitter.off(ModelEvent.AfterTableBeforeFetch, this.preselectPeriod);
    }

    onAfterLoad = async () => {
        const { storage } = this.props;

        const isAuditTrail = storage.formMode === FormMode.AuditTrail;

        if (!isAuditTrail) {
            const promises: Promise<unknown>[] = [];

            if (!storage.data.entity.NumberOurs) {
                promises.push(setMatchingNumberRange(storage, true));
            } else {
                promises.push(loadAndStoreRanges(storage));
            }

            if (!storage.data.entity.Year || !storage.data.entity.Month) {
                this.preselectPeriod();
            } else {
                storage.setValueByPath(MonthYearLocalPath, createDateFromPeriod(storage.data.entity));
            }

            await Promise.all(promises);
        }

        return super.onAfterLoad();
    };

    onBeforeSave(data?: IPrSalaryAdvanceEntity): IPrSalaryAdvanceEntity {
        const ret = super.onBeforeSave(data) ?? this.props.storage.data.entity;
        if (this.props.storage.data.bindingContext.isNew()) {
            const period = createPeriodFromDate((ret as unknown as IEntity)[MonthYearLocalPath]);
            ret.Month = period.Month;
            ret.Year = period.Year;
        }
        return ret;
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean): void {
        if (isNew) {
            // switch to the mohth/year of the created salary advance
            this.switchTableToCurrentPeriod();
        }
        super.onAfterSave(isNew, preventTableViewRefresh);
    }

    async switchTableToCurrentPeriod(first = true): Promise<void> {
        const tableStorage = (this.props.tableStorage as TableStorageWithPayrollPeriods);
        const { payrollPeriods } = tableStorage.getCustomData();
        const { entity } = this.props.storage.data;
        const _getPeriod = (periods: IPrPayrollPeriodEntity[]) =>
            periods?.find(pp => pp.Month === entity.Month && pp.Year === entity.Year);
        const selectedPeriod = _getPeriod(payrollPeriods);
        if (!selectedPeriod && first) {
            await preselectPayrollPeriods(tableStorage);
            // second try after refreshing periods
            this.switchTableToCurrentPeriod(false);
        }
        if (selectedPeriod) {
            tableStorage.setCustomData({ selectedPeriod });
            tableStorage.store({ customSecondaryFilter: getSalaryAdvancePeriodFilter(selectedPeriod) });
            tableStorage.refresh(true);
        }
    }

    preselectPeriod = (): void => {
        const { selectedPeriod, payrollPeriods } = (this.props.tableStorage as TableStorageWithPayrollPeriods).getCustomData();
        if (!selectedPeriod) {
            // wait for PayrollPeriods load...
            this.props.tableStorage.emitter.on(ModelEvent.AfterTableBeforeFetch, this.preselectPeriod);
            return;
        }
        const { storage } = this.props;
        storage.setCustomData({ existingPeriods: payrollPeriods });
        if (selectedPeriod) {
            const dt = createDateFromPeriod(selectedPeriod);
            storage.setValueByPath(MonthYearLocalPath, dt);
            storage.refresh();
        }
    };

    handleChange = (e: ISmartFieldChange): void => {
        this.props.storage.handleChange(e);
        handleCurrentTemporalPropChange(e, this.props.storage);
        this.props.storage.refreshFields(e.triggerAdditionalTasks);
    };
}

export default withPermissionContext(SalaryAdvanceFormView);